import { Select as AntSelect } from "antd";
import React, { Fragment, memo, useCallback, useEffect, useState } from "react";
import { Autocomplete, Grid, TextField } from "@mui/material";
import {
  getAllClientBoxes,
  getAllClientBrands,
  getAllClientProducerFarms,
  getAllClientProducers,
  getClientIdByToken,
  validTaskCodeByToken,
} from "../../../../libs/apis";
import { useSnackbar } from "@components";
import {
  Button,
  Divider,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  TimePicker,
} from "antd";
import { useWorkflowTruckStore } from "../../store";
import {
  ClientI,
  ClientProducerI,
  ClientProducerFarmI,
  ClientBoxI,
  ClientBrandI,
} from "../../../../libs/interfaces";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DateFormatHelper } from "../../../../libs/helpers";
import { Environments } from "../../../../libs/enums";
import { useShallow } from "zustand/react/shallow";
import { DividerComponent } from "../../../../libs/shared";

dayjs.extend(customParseFormat);
const hourFormat = "HH:mm";

interface FirstComponentProps {
  clients: ClientI[];
  width: string;
}

interface PropsGenericI {
  width: string;
}

export const FirstComponent = memo(
  ({ clients, width }: FirstComponentProps) => {
    const [showExporters, setShowExporters] = useState(false);

    useEffect(() => {
      handleGetData();
    }, []);

    const handleGetData = async () => {
      const { data: dataTask } = await validTaskCodeByToken(
        Environments.TASK_TRUCK_ASSIGN
      );
      if (!dataTask) return;
      if (dataTask.status) setShowExporters(true);
    };

    return (
      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignContent="flex-start"
        alignItems="flex-start"
        sx={{
          minHeight: `calc(100vh - 105px)`,
          maxHeight: `calc(100vh - 105px)`,
          height: `calc(100vh - 105px)`,
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          pt: 7,
          width: "100%",
        }}
      >
        <NumeroContenedor />

        {showExporters ? null : (
          <Fragment>
            {width === "xs" ? null : <Grid item xs={3}></Grid>}
          </Fragment>
        )}

        <Exporters clients={clients} showExporters={showExporters} />

        <Magap />
        <Cajas />
        <CajasEstiba />
        <DividerComponent label="TRANSPORTE" />
        <Conductor width={width} />
        <CompañíaDeTransporte />

        <DividerComponent label="CALIDAD - GANCHO" />
        <Calidad width={width} />

        <Gancho width={width} />

        <DividerComponent label="HORAS DE PROCESO" />
        <HorasProceso />
        <DividerComponent label="OBSERVACIÓN" />
        <Observacion />
        <Grid item xs={12} sx={{ height: 20 }}></Grid>
      </Grid>
    );
  }
);

const NumeroContenedor = () => {
  const container = useWorkflowTruckStore((state) => state.container);
  const setContainer = useWorkflowTruckStore((state) => state.setContainer);
  const getStatusT = useWorkflowTruckStore(
    useShallow((state) => state.getStatusT)
  );

  return (
    <Grid item xs={12} sm={6} md={6}>
      <Input
        addonBefore={<p style={{ fontWeight: "bold" }}>N° Contenedor</p>}
        value={container}
        onChange={(e) => {
          setContainer(e.target.value.toUpperCase());
        }}
        onBlur={(e) => {
          setContainer(e.target.value.trim());
          getStatusT();
        }}
      />
    </Grid>
  );
};

interface ClientsProps {
  clients: ClientI[];
  showExporters: boolean;
}

const Exporters = memo(({ clients, showExporters }: ClientsProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const client = useWorkflowTruckStore((state) => state.client);
  const clientId = useWorkflowTruckStore((state) => state.clientId);
  const producerId = useWorkflowTruckStore((state) => state.producerId);

  const setClient = useWorkflowTruckStore((state) => state.setClient);
  const setClientId = useWorkflowTruckStore((state) => state.setClientId);
  const resetClient = useWorkflowTruckStore((state) => state.resetClient);
  const getStatusT = useWorkflowTruckStore((state) => state.getStatusT);

  const [producers, setProducers] = useState<ClientProducerI[]>([]);
  const [farms, setFarms] = useState<ClientProducerFarmI[]>([]);
  const [boxes, setBoxes] = useState<ClientBoxI[]>([]);
  const [brands, setBrands] = useState<ClientBrandI[]>([]);

  const handleSelectClient = async (e: any, newValue: string | null) => {
    try {
      setClient(newValue ?? "");
      resetClient();
      getStatusT();

      if (!newValue) {
        setClient("");
        setClientId(null);
        getProducers(null);
        getFarms(null);
        return;
      }

      const fill = clients.find((el) => el.name === newValue);
      if (!fill) return;

      setClientId(fill.id!);

      getDatasClient(fill.id!);
    } catch (err: any) {
      enqueueSnackbar(err.toString, { variant: "error" });
    }
  };

  useEffect(() => {
    handleGetData();
  }, []);

  const handleGetData = async () => {
    let elclientId: number | null = null;

    if (clientId) {
      getDatasClient(clientId);
      elclientId = clientId!;
    }

    if (!elclientId) {
      const { data: dataClient } = await getClientIdByToken();
      if (dataClient) {
        setClientId(dataClient);
        getDatasClient(dataClient);
        elclientId = dataClient;
      }
    }

    if (producerId) {
      getFarms(producerId);
    }
  };

  const getDatasClient = useCallback(async (client: number) => {
    try {
      getProducers(client);

      const { data: dataBox } = await getAllClientBoxes(client);
      if (!dataBox) return;
      setBoxes(dataBox);

      const { data: dataBrands } = await getAllClientBrands(client);
      if (!dataBrands) return;
      setBrands(dataBrands);
    } catch (err) {
      console.log(err);
    }
  }, []);

  const getFarms = useCallback(async (clientProducer: number | null) => {
    try {
      if (clientProducer === null) {
        setFarms([]);
        return;
      }

      const { data: dataFarms } = await getAllClientProducerFarms(
        clientProducer
      );
      if (!dataFarms) return;
      setFarms(dataFarms);
    } catch (err) {
      console.log(err);
    }
  }, []);

  const getProducers = useCallback(async (clientId: number | null) => {
    try {
      if (clientId === null) {
        setProducers([]);
        return;
      }

      const { data: dataProducers } = await getAllClientProducers(clientId);
      if (!dataProducers) return;
      setProducers(dataProducers);
    } catch (err) {
      console.log(err);
    }
  }, []);

  return (
    <Fragment>
      {showExporters ? (
        <Grid item xs={12} sm={6} md={6}>
          <AntSelect
            showSearch
            allowClear
            size="middle"
            placeholder="Exportador"
            style={{ width: "100%" }}
            optionFilterProp="label"
            options={clients.map((el: ClientI) => ({
              label: el.name,
              value: el.name,
            }))}
            value={client || undefined}
            onChange={(value) => handleSelectClient(null, value ?? null)}
          />
        </Grid>
      ) : null}
      <DividerComponent label="EXPORTADORA" />
      <Producers
        producers={producers}
        getFarms={getFarms}
        enqueueSnackbar={enqueueSnackbar}
      />
      <Farms farms={farms} />
      <Brands brands={brands} />
      <Boxes boxes={boxes} />
    </Fragment>
  );
});

interface ProducersPropsI {
  producers: ClientProducerI[];
  getFarms: (clientProducer: number | null) => void;
  enqueueSnackbar: (message: string, options?: any) => void;
}

const Producers = memo(
  ({ producers, getFarms, enqueueSnackbar }: ProducersPropsI) => {
    const producer = useWorkflowTruckStore((state) => state.producer);
    const setProducer = useWorkflowTruckStore((state) => state.setProducer);
    const setProducerId = useWorkflowTruckStore((state) => state.setProducerId);
    const resetProducer = useWorkflowTruckStore((state) => state.resetProducer);
    const getStatusT = useWorkflowTruckStore(
      useShallow((state) => state.getStatusT)
    );

    const handleSelectClientProducer = async (newValue: string | null) => {
      try {
        setProducer(newValue ?? "");
        resetProducer();

        if (!newValue) {
          getFarms(null);
          getStatusT();
          return;
        }

        const fill = producers.find((el) => el.name === newValue);
        if (!fill) return;

        setProducerId(fill.id!);

        getFarms(fill.id!);
      } catch (err: any) {
        enqueueSnackbar(err.toString, { variant: "error" });
      }
    };

    return (
      <Grid item xs={6}>
        <Select
          options={producers.map((el) => ({ label: el.name, value: el.name }))}
          value={producer}
          placeholder="Productor"
          suffixIcon={
            producer ? (
              <p style={{ fontWeight: "bold", fontSize: 6, color: "black" }}>
                Productor
              </p>
            ) : (
              <p style={{ fontWeight: "bold", color: "black" }}>Productor</p>
            )
          }
          allowClear={true}
          onChange={handleSelectClientProducer}
          style={{ width: "100%" }}
        />
      </Grid>
    );
  }
);

interface FarmsPropsI {
  farms: ClientProducerFarmI[];
}

const Farms = memo(({ farms }: FarmsPropsI) => {
  const balerCode = useWorkflowTruckStore((state) => state.balerCode);
  const farm = useWorkflowTruckStore((state) => state.farm);
  const zone = useWorkflowTruckStore((state) => state.zone);
  const setFarm = useWorkflowTruckStore((state) => state.setFarm);
  const setFarmId = useWorkflowTruckStore((state) => state.setFarmId);
  const setBalerCode = useWorkflowTruckStore((state) => state.setBalerCode);
  const setZone = useWorkflowTruckStore((state) => state.setZone);

  const resetFarm = useWorkflowTruckStore((state) => state.resetFarm);
  const getStatusT = useWorkflowTruckStore(
    useShallow((state) => state.getStatusT)
  );

  const handleSelectClientProducerFarm = (newValue: string | null) => {
    setFarm(newValue ?? "");

    if (!newValue) {
      resetFarm();
      getStatusT();
      return;
    }

    const fill = farms.find((el) => el.name === newValue);
    if (!fill) return;

    setFarmId(fill.id!);
    setZone(fill.zone);
    setBalerCode(fill.balerCode);
    getStatusT();
  };

  return (
    <Fragment>
      <Grid item xs={6}>
        <Select
          options={farms.map((el) => ({ label: el.name, value: el.name }))}
          value={farm}
          placeholder="Hacienda"
          suffixIcon={
            farm ? (
              <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
                Hacienda
              </p>
            ) : (
              <p style={{ fontWeight: "bold", color: "black" }}>Hacienda</p>
            )
          }
          allowClear={true}
          onChange={handleSelectClientProducerFarm}
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={zone}
          readOnly
          suffix={
            zone ? (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 8 }}>
                Zona
              </p>
            ) : (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 11 }}>
                Zona
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={balerCode}
          onChange={(e) => setBalerCode(e.target.value)}
          // readOnly
          suffix={
            balerCode ? (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 8 }}>
                Empacadora
              </p>
            ) : (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 11 }}>
                Código de Empacadora
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
    </Fragment>
  );
});

const Brands = ({ brands }: { brands: ClientBrandI[] }) => {
  const brand = useWorkflowTruckStore((state) => state.brand);
  const setBrand = useWorkflowTruckStore((state) => state.setBrand);
  const setBrandId = useWorkflowTruckStore((state) => state.setBrandId);
  const getStatusT = useWorkflowTruckStore(
    useShallow((state) => state.getStatusT)
  );

  const handleSelectBrand = (newValue: string | null) => {
    if (!newValue) {
      setBrand("");
      setBrandId(null);
      getStatusT();
      return;
    }

    setBrand(newValue);

    const fill = brands.find((el) => el.name === newValue);
    if (!fill) return;

    setBrandId(fill.id!);
    getStatusT();
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={brands.map((el) => ({ label: el.name, value: el.name }))}
        value={brand}
        placeholder="Marca"
        suffixIcon={
          brand ? (
            <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
              Marca
            </p>
          ) : (
            <p style={{ fontWeight: "bold", color: "black" }}>Marca</p>
          )
        }
        allowClear={true}
        onChange={handleSelectBrand}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const Boxes = ({ boxes }: { boxes: ClientBoxI[] }) => {
  const box = useWorkflowTruckStore((state) => state.box);
  const setBox = useWorkflowTruckStore((state) => state.setBox);
  const setBoxId = useWorkflowTruckStore((state) => state.setBoxId);
  const getStatusT = useWorkflowTruckStore(
    useShallow((state) => state.getStatusT)
  );

  const handleSelectBox = (newValue: string | null) => {
    if (!newValue) {
      setBox("");
      setBoxId(null);
      getStatusT();
      return;
    }

    setBox(newValue);

    const fill = boxes.find((el) => el.name === newValue);
    if (!fill) return;

    setBoxId(fill.id!);
    getStatusT();
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={boxes.map((el) => ({ label: el.name, value: el.name }))}
        value={box}
        placeholder="Tipo de Caja"
        suffixIcon={
          box ? (
            <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
              Caja
            </p>
          ) : (
            <p style={{ fontWeight: "bold", color: "black" }}>Tipo de Caja</p>
          )
        }
        allowClear={true}
        onChange={handleSelectBox}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const Magap = () => {
  const magap = useWorkflowTruckStore((state) => state.magap);
  const setMagap = useWorkflowTruckStore((state) => state.setMagap);
  const getStatusT = useWorkflowTruckStore(
    useShallow((state) => state.getStatusT)
  );

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Input
        style={{ width: "100%" }}
        value={magap || ""}
        onChange={(e) => setMagap(e?.target?.value?.toUpperCase() ?? "")}
        prefix={
          magap ? (
            <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
              Magap
            </p>
          ) : (
            <p style={{ fontWeight: "bold", color: "black" }}>Magap</p>
          )
        }
      />
    </Grid>
  );
};

const Cajas = () => {
  const numCajas = useWorkflowTruckStore((state) => state.numCajas);
  const setNumCajas = useWorkflowTruckStore((state) => state.setNumCajas);
  const getStatusT = useWorkflowTruckStore(
    useShallow((state) => state.getStatusT)
  );

  return (
    <Grid item xs={6}>
      <InputNumber
        style={{ width: "100%" }}
        value={numCajas || null}
        onChange={(value) => setNumCajas(value)}
        onBlur={(e) => getStatusT()}
        addonBefore={
          <p
            style={{
              fontSize: "12px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
            }}
          >
            N° Cajas
          </p>
        }
      />
    </Grid>
  );
};

const CajasEstiba = () => {
  const cajasEstiba = useWorkflowTruckStore((state) => state.cajasEstiba);
  const setCajasEstiba = useWorkflowTruckStore((state) => state.setCajasEstiba);
  const getStatusT = useWorkflowTruckStore(
    useShallow((state) => state.getStatusT)
  );

  return (
    <Grid item xs={6}>
      <InputNumber
        style={{ width: "100%" }}
        value={cajasEstiba || null}
        onChange={(value) => setCajasEstiba(value)}
        onBlur={(e) => getStatusT()}
        addonBefore={
          <p
            style={{
              fontSize: "10px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
              textWrap: "wrap",
            }}
          >
            Cajas Doble Estiba
          </p>
        }
      />
    </Grid>
  );
};

const Conductor = ({ width }: { width: string }) => {
  const driver = useWorkflowTruckStore((state) => state.driver);
  const setDriver = useWorkflowTruckStore((state) => state.setDriver);

  const placa = useWorkflowTruckStore((state) => state.placa);
  const setPlaca = useWorkflowTruckStore((state) => state.setPlaca);

  return (
    <Fragment>
      {width === "xs" ? (
        <Fragment>
          <Grid item xs={12}>
            <Input
              value={driver || ""}
              onChange={(e) => setDriver(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Conductor
                </p>
              }
              placeholder="Nombre y Apellido"
              style={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={placa || ""}
              onChange={(e) => setPlaca(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Placa
                </p>
              }
              placeholder=""
              style={{ width: "100%" }}
            />
          </Grid>
        </Fragment>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={driver || ""}
              onChange={(e) => setDriver(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Conductor
                </p>
              }
              placeholder="Nombre y Apellido"
              style={{ width: "60%" }}
            />
            <Input
              value={placa || ""}
              onChange={(e) => setPlaca(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Placa
                </p>
              }
              placeholder=""
              style={{ width: "40%" }}
            />
          </Space.Compact>
        </Grid>
      )}
    </Fragment>
  );
};

const CompañíaDeTransporte = () => {
  const companyTransport = useWorkflowTruckStore(
    (state) => state.companyTransport
  );
  const setCompanyTransport = useWorkflowTruckStore(
    (state) => state.setCompanyTransport
  );

  return (
    <Grid item xs={12}>
      <Input
        value={companyTransport || ""}
        onChange={(e) => setCompanyTransport(e.target.value)}
        addonBefore={
          <p
            style={{
              fontSize: "14px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
            }}
          >
            Compañía
          </p>
        }
        placeholder=""
      />
    </Grid>
  );
};

const Calidad: React.FC<PropsGenericI> = ({ width }) => {
  const calidad = useWorkflowTruckStore((state) => state.calidad);
  const setCalidad = useWorkflowTruckStore((state) => state.setCalidad);
  const calidadCi = useWorkflowTruckStore((state) => state.calidadCi);
  const setCalidadCi = useWorkflowTruckStore((state) => state.setCalidadCi);

  return (
    <Fragment>
      {width === "xs" ? (
        <Fragment>
          <Grid item xs={12}>
            <Input
              value={calidad || ""}
              onChange={(e) => setCalidad(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Calidad
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={calidadCi || ""}
              onChange={(e) => setCalidadCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Grid>
          <Divider dashed />
        </Fragment>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={calidad || ""}
              onChange={(e) => setCalidad(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Calidad
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
            <Input
              value={calidadCi || ""}
              onChange={(e) => setCalidadCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Space.Compact>
        </Grid>
      )}
    </Fragment>
  );
};

const Gancho: React.FC<PropsGenericI> = ({ width }) => {
  const gancho = useWorkflowTruckStore((state) => state.gancho);
  const setGancho = useWorkflowTruckStore((state) => state.setGancho);
  const ganchoCi = useWorkflowTruckStore((state) => state.ganchoCi);
  const setGanchoCi = useWorkflowTruckStore((state) => state.setGanchoCi);

  return (
    <Fragment>
      {width === "xs" ? (
        <Fragment>
          <Grid item xs={12} sx={{ mt: -2 }}>
            <Input
              value={gancho || ""}
              onChange={(e) => setGancho(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Gancho
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={ganchoCi || ""}
              onChange={(e) => setGanchoCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Grid>
        </Fragment>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={gancho || ""}
              onChange={(e) => setGancho(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Gancho
                </p>
              }
              placeholder="Nombre Y Apellido"
            />

            <Input
              value={ganchoCi || ""}
              onChange={(e) => setGanchoCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Space.Compact>
        </Grid>
      )}
    </Fragment>
  );
};

const HorasProceso = () => {
  const startProcess = useWorkflowTruckStore((state) => state.startProcess);
  const closeProcess = useWorkflowTruckStore((state) => state.closeProcess);
  const endLoading = useWorkflowTruckStore((state) => state.endLoading);
  const containerClose = useWorkflowTruckStore((state) => state.containerClose);
  const containerOulet = useWorkflowTruckStore((state) => state.containerOulet);
  const setStartProcess = useWorkflowTruckStore(
    (state) => state.setStartProcess
  );
  const setCloseProcess = useWorkflowTruckStore(
    (state) => state.setCloseProcess
  );
  const setEndLoading = useWorkflowTruckStore((state) => state.setEndLoading);
  const setContainerClose = useWorkflowTruckStore(
    (state) => state.setContainerClose
  );
  const setContainerOulet = useWorkflowTruckStore(
    (state) => state.setContainerOulet
  );

  const handleTimeChangeStart = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Cambiar el formato de la hora
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setStartProcess(value); // Actualizar el valor del input
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlurStart = () => {
    if (startProcess === "") {
      setStartProcess(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(startProcess)) {
      setStartProcess(startProcess); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setStartProcess(startProcess || ""); // Revertir al valor anterior si es inválido
    }
  };

  const handleTimeChangeClose = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Cambiar el formato de la hora
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setCloseProcess(value); // Actualizar el valor del input
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlurClose = () => {
    if (closeProcess === "") {
      setCloseProcess(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(closeProcess)) {
      setCloseProcess(closeProcess); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setCloseProcess(closeProcess || ""); // Revertir al valor anterior si es inválido
    }
  };

  const handleTimeChangeEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Cambiar el formato de la hora
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setEndLoading(value); // Actualizar el valor del input
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlurEnd = () => {
    if (endLoading === "") {
      setEndLoading(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(endLoading)) {
      setEndLoading(endLoading); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setEndLoading(endLoading || ""); // Revertir al valor anterior si es inválido
    }
  };

  const handleTimeChangeCloseTwo = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Cambiar el formato de la hora
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setContainerClose(value); // Actualizar el valor del input
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlurCloseTwo = () => {
    if (containerClose === "") {
      setContainerClose(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(containerClose)) {
      setContainerClose(containerClose); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setContainerClose(containerClose || ""); // Revertir al valor anterior si es inválido
    }
  };

  const handleTimeChangeOulet = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Cambiar el formato de la hora
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setContainerOulet(value); // Actualizar el valor del input
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlurOulet = () => {
    if (containerOulet === "") {
      setContainerOulet(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(containerOulet)) {
      setContainerOulet(containerOulet); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setContainerOulet(containerOulet || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Fragment>
      <Grid item xs={12} sm={6}>
        <Space.Compact style={{ width: "100%" }}>
          <Button
            onClick={() => {
              const dateFormat = new DateFormatHelper();
              setStartProcess(dateFormat.getTime());
            }}
            style={{ width: "150px" }}
          >
            Inicio de Proceso
          </Button>
          <Input
            style={{ width: "calc(100% - 150px)" }}
            placeholder="HH:mm (ej. 14:30)"
            value={startProcess || ""}
            onChange={handleTimeChangeStart}
            onBlur={handleBlurStart}
            maxLength={5} // Limitar a 5 caracteres (HH:mm)
          />
        </Space.Compact>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Space.Compact style={{ width: "100%" }}>
          <Button
            onClick={() => {
              const dateFormat = new DateFormatHelper();
              setCloseProcess(dateFormat.getTime());
            }}
            style={{ width: "150px" }}
          >
            Cierre de Proceso
          </Button>
          <Input
            style={{ width: "calc(100% - 150px)" }}
            placeholder="HH:mm (ej. 14:30)"
            value={closeProcess || ""}
            onChange={handleTimeChangeClose}
            onBlur={handleBlurClose}
            maxLength={5} // Limitar a 5 caracteres (HH:mm)
          />
        </Space.Compact>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Space.Compact style={{ width: "100%" }}>
          <Button
            onClick={() => {
              const dateFormat = new DateFormatHelper();
              setEndLoading(dateFormat.getTime());
            }}
            style={{ width: "150px" }}
          >
            Fin de Carga
          </Button>
          <Input
            style={{ width: "calc(100% - 150px)" }}
            placeholder="HH:mm (ej. 14:30)"
            value={endLoading || ""}
            onChange={handleTimeChangeEnd}
            onBlur={handleBlurEnd}
            maxLength={5} // Limitar a 5 caracteres (HH:mm)
          />
        </Space.Compact>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Space.Compact style={{ width: "100%" }}>
          <Button
            onClick={() => {
              const dateFormat = new DateFormatHelper();
              setContainerClose(dateFormat.getTime());
            }}
            style={{ width: "150px" }}
          >
            Cierre Camión
          </Button>
          <Input
            style={{ width: "calc(100% - 150px)" }}
            placeholder="HH:mm (ej. 14:30)"
            value={containerClose || ""}
            onChange={handleTimeChangeCloseTwo}
            onBlur={handleBlurCloseTwo}
            maxLength={5} // Limitar a 5 caracteres (HH:mm)
          />
        </Space.Compact>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Space.Compact style={{ width: "100%" }}>
          <Button
            onClick={() => {
              const dateFormat = new DateFormatHelper();
              setContainerOulet(dateFormat.getTime());
            }}
            style={{ width: "150px" }}
          >
            Salida Camión
          </Button>
          <Input
            style={{ width: "calc(100% - 150px)" }}
            placeholder="HH:mm (ej. 14:30)"
            value={containerOulet || ""}
            onChange={handleTimeChangeOulet}
            onBlur={handleBlurOulet}
            maxLength={5} // Limitar a 5 caracteres (HH:mm)
          />
        </Space.Compact>
      </Grid>
    </Fragment>
  );
};

const Observacion = () => {
  const observation = useWorkflowTruckStore((state) => state.observation);

  const setObservation = useWorkflowTruckStore((state) => state.setObservation);

  const getStatusT = useWorkflowTruckStore((state) => state.getStatusT);

  return (
    <Grid item xs={12}>
      <TextField
        fullWidth
        id="observation"
        name="observation"
        label="Observación"
        size="medium"
        multiline
        value={observation || ""}
        onChange={(e) => setObservation(e.target.value)}
        onBlur={(e) => {
          getStatusT();
        }}
      />
    </Grid>
  );
};

const validateTimeFormat = (time: string) => {
  const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
  return timeRegex.test(time);
};
